import moment from 'moment'
import GeoContext from 'contexts/geoContext'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useLocation, useParams } from 'react-router-dom'
import { useAppSelector } from 'hooks/reduxHooks'
import useOrder from 'hooks/Order/useOrder'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ChatTripPlannerMobileBlocker from './ChatTripPlannerMobileBlocker'
import { getFirstName, isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { isSpoofed as isSpoofedSelector } from 'selectors/isSpoofed'
import { canAccessStaffChat } from 'tripPlanner/utils/staffChatAccess'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useAnonymousChatClaiming } from 'tripPlanner/assistant/hooks/useAnonymousChatClaiming'
import { useAssistantHistory } from 'tripPlanner/assistant/hooks/useAssistantHistory'
import { NEW_CHAT_ROUTE_SEGMENT, useChatNavigation } from './ChatTripPlannerPage/hooks/useChatNavigation'
import { useChatTitleGeneration } from './ChatTripPlannerPage/hooks/useChatTitleGeneration'
import TripChatRuntime from './ChatTripPlannerPage/TripChatRuntime'
import TripChatSurfaceRouter from './ChatTripPlannerPage/TripChatSurfaceRouter'
import type { TripChatDesktopSurfaceProps } from './ChatTripPlannerPage/surfaces/TripChatDesktopSurface'
import type { TripChatMobileSurfaceProps } from './ChatTripPlannerPage/surfaces/TripChatMobileSurface'
import { buildBookingTag } from './ChatLandingPage/bookingSelectionUtils'
import { getUpcomingTrip } from './ChatLandingPage/upcomingTrips'
import { useCanAccessMobileChat } from 'tripPlanner/hooks/useShouldUseChat'
import { getBrowserTimeZone } from 'lib/datetime/dateUtils'
import type { AssistantUserData } from 'tripPlanner/assistant/api/types'
import { useChatHandoffRedemption } from './ChatTripPlannerPage/hooks/useChatHandoffRedemption'
import ChatHandoffLoading from './ChatTripPlannerPage/components/ChatHandoffLoading'
import RefreshChatListAfterHandoffTurn from './ChatTripPlannerPage/components/RefreshChatListAfterHandoffTurn'

function ChatTripPlannerPage() {
  const isMobileOrTablet = useIsMobileOrTablet()
  const isMobileViewport = useScreenSizeOnly('mobile')
  const shouldUseMobileSurface = isMobileOrTablet || isMobileViewport
  const canAccessMobileChat = useCanAccessMobileChat()

  if (shouldUseMobileSurface && !canAccessMobileChat) {
    return <ChatTripPlannerMobileBlocker />
  }

  return <ChatTripPlannerPageContent shouldUseMobileSurface={shouldUseMobileSurface} />
}

interface ChatTripPlannerPageContentProps {
  shouldUseMobileSurface: boolean
}

function ChatTripPlannerPageContent(props: ChatTripPlannerPageContentProps) {
  const { shouldUseMobileSurface } = props
  const intl = useIntl()

  const isStaff = useAppSelector(isLEStaff)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(isSpoofedSelector)
  const spoofingAgentEmail = useAppSelector(state => state.auth.account.spoofedBy?.email)
  const accountEmail = useAppSelector(state => state.auth.account.email)
  const canViewStaffChat = isLoggedIn && isStaff && canAccessStaffChat(accountEmail)
  // Tracks the conversation a staff member opened via the "Spoof conversation"
  // form so we can surface a banner while they view a customer's chat.
  const [staffViewedChatId, setStaffViewedChatId] = useState<number | undefined>(undefined)
  const firstName = useAppSelector(getFirstName)
  const { latitude, longitude, currentRegionCode } = useContext(GeoContext)
  const { search } = useLocation()
  const timezone = getBrowserTimeZone() || undefined
  const userData = useMemo<AssistantUserData>(() => ({
    firstName: firstName || undefined,
    lat: latitude,
    lng: longitude,
    timezone,
    region: currentRegionCode,
    chatUiLayout: shouldUseMobileSurface ? 'mobile' : 'desktop',
  }), [firstName, latitude, longitude, timezone, currentRegionCode, shouldUseMobileSurface])

  const [isDevMode, setIsDevMode] = useState(false)
  const [isRewindMode, setIsRewindMode] = useState(false)
  const [dismissedPrefilledOrderId, setDismissedPrefilledOrderId] = useState<string>()
  const { chatId: routeChatIdParam } = useParams<{ chatId?: string }>()
  const orderIdFromQuery = useMemo(() => new URLSearchParams(search).get('orderId') ?? undefined, [search])
  const prefilledOrderId = orderIdFromQuery !== dismissedPrefilledOrderId ? orderIdFromQuery : undefined
  const [orderForChip] = useOrder(orderIdFromQuery)
  const [today] = useState(() => moment().format(ISO_DATE_FORMAT))

  const {
    chatId, setChatId: syncChatId, setChatIdForStreaming,
    isNewChatRoute, isLandingRoute, existingRouteChatId, isChatIdSyncPending, navigateToNewChatRoute,
  } = useChatNavigation({ routeChatId: routeChatIdParam })
  const { isRedeemingChatHandoff, handoffChatId, redeemedHandoffChatId } = useChatHandoffRedemption({ search, userData, setChatId: syncChatId })

  const chatKey = useMemo(
    () => (chatId !== undefined ? String(chatId) : NEW_CHAT_ROUTE_SEGMENT),
    [chatId],
  )

  const prefilledOrderTag = useMemo(() => {
    const tile = orderForChip && getUpcomingTrip(orderForChip, today)
    return tile && buildBookingTag({ intl, tile })
  }, [intl, orderForChip, today])

  const prefilledOrderChipLabels = useMemo(() => {
    return prefilledOrderTag && prefilledOrderId ? [prefilledOrderTag] : []
  }, [prefilledOrderId, prefilledOrderTag])

  const effectiveOrderIds = useMemo(() => (prefilledOrderId ? [prefilledOrderId] : []), [prefilledOrderId])

  const {
    refetchChatList, claimChatsMutation,
    updateChatLastActivityTimestamp, updateChatMetadata, startChatTitleGeneration: startTitleGen,
  } = useAssistantHistory({ isLoggedIn })

  const { startChatTitleGeneration } = useChatTitleGeneration({ startTitleGen })

  const { waitForClaimCompletion, anonymousClaimTransfer } = useAnonymousChatClaiming({
    isLoggedIn,
    currentChatId: chatId,
    claimChatsMutation,
    refetchChatList,
  })

  const handleDevModeToggle = useCallback(() => {
    setIsDevMode((prev) => !prev)
  }, [])

  const handleRewindModeToggle = useCallback(() => {
    setIsRewindMode((prev) => !prev)
  }, [])

  const handleRemovePrefilledOrderTag = useCallback(() => {
    setDismissedPrefilledOrderId(orderIdFromQuery)
  }, [orderIdFromQuery])

  const desktopSurfaceProps = useMemo<TripChatDesktopSurfaceProps>(() => ({
    chatId,
    syncChatId,
    isNewChatRoute,
    isLandingRoute,
    existingRouteChatId,
    handoffChatId,
    navigateToNewChatRoute,
    isLoggedIn,
    isDevMode,
    isRewindMode,
    isSpoofed,
    spoofingAgentEmail,
    canViewStaffChat,
    staffViewedChatId,
    onStaffViewedChatIdChange: setStaffViewedChatId,
    onDevModeToggle: handleDevModeToggle,
    onRewindModeToggle: handleRewindModeToggle,
    prefilledOrderId,
    prefilledOrderChipLabels,
    onRemovePrefilledOrderTag: handleRemovePrefilledOrderTag,
  }), [
    canViewStaffChat,
    chatId,
    existingRouteChatId,
    handleDevModeToggle,
    handleRemovePrefilledOrderTag,
    handleRewindModeToggle,
    handoffChatId,
    isDevMode,
    isLandingRoute,
    isLoggedIn,
    isNewChatRoute,
    isRewindMode,
    isSpoofed,
    navigateToNewChatRoute,
    prefilledOrderChipLabels,
    prefilledOrderId,
    spoofingAgentEmail,
    staffViewedChatId,
    syncChatId,
  ])

  const mobileSurfaceProps = useMemo<TripChatMobileSurfaceProps>(() => ({
    chatId,
    syncChatId,
    isNewChatRoute,
    isLandingRoute,
    existingRouteChatId,
    handoffChatId,
    navigateToNewChatRoute,
    isLoggedIn,
    isStaff,
    isDevMode,
    isRewindMode,
    isSpoofed,
    spoofingAgentEmail,
    onStaffViewedChatIdChange: setStaffViewedChatId,
    onDevModeToggle: handleDevModeToggle,
    onRewindModeToggle: handleRewindModeToggle,
    prefilledOrderId,
    prefilledOrderChipLabels,
    onRemovePrefilledOrderTag: handleRemovePrefilledOrderTag,
  }), [
    chatId,
    existingRouteChatId,
    handleDevModeToggle,
    handleRemovePrefilledOrderTag,
    handleRewindModeToggle,
    handoffChatId,
    isDevMode,
    isLandingRoute,
    isLoggedIn,
    isNewChatRoute,
    isRewindMode,
    isSpoofed,
    isStaff,
    navigateToNewChatRoute,
    prefilledOrderChipLabels,
    prefilledOrderId,
    spoofingAgentEmail,
    syncChatId,
  ])

  if (isRedeemingChatHandoff) {
    return <ChatHandoffLoading />
  }

  return (
    <TripChatRuntime
      chatId={chatId}
      isChatIdSyncPending={isChatIdSyncPending}
      setChatIdForStreaming={setChatIdForStreaming}
      isDevMode={isDevMode}
      canStaffRewind={canViewStaffChat && isStaff && isRewindMode && chatId !== undefined && chatId === staffViewedChatId}
      navigateToChat={syncChatId}
      isLoggedIn={isLoggedIn}
      orderIds={effectiveOrderIds}
      refetchChatList={refetchChatList}
      waitForAnonymousChatClaim={waitForClaimCompletion}
      anonymousClaimTransfer={anonymousClaimTransfer}
      startChatTitleGeneration={startChatTitleGeneration}
      updateChatLastActivityTimestamp={updateChatLastActivityTimestamp}
      updateChatMetadata={updateChatMetadata}
      userData={userData}
      chatKey={chatKey}
    >
      {isLoggedIn && <RefreshChatListAfterHandoffTurn handoffChatId={redeemedHandoffChatId} refetchChatList={refetchChatList} />}
      {shouldUseMobileSurface && (
        <TripChatSurfaceRouter
          surface="mobile"
          mobileSurfaceProps={mobileSurfaceProps}
        />
      )}
      {!shouldUseMobileSurface && (
        <TripChatSurfaceRouter
          surface="desktop"
          desktopSurfaceProps={desktopSurfaceProps}
        />
      )}
    </TripChatRuntime>
  )
}

export default ChatTripPlannerPage
